import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Grid,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import {
  DiagnosisMetersReadDTO,
  DiagnosisMetersCreateDTO,
  DiagnosisEnergyVectorsReadDTO,
  IDiagnosisMetersCreateDTO,
  EnergyVectorsReadDTO,
  MeterType,
  DiagnosisSelfProductionPlantsReadDTO,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { ActionButton } from '../../common';
import { EnergyUseDropdown } from '../../common/EnergyUseDropdown';
import { EnergyVectorDropdown } from '../../common/EnergyVectorDropdown';
import { useSelfProductionPlantsByDiagnosisId } from '../../../hooks/useSelfProductionPlants';
import { invalidateConsumptionTotalsQueries } from '../../../hooks';
import { FUNCTIONAL_AREA_OPTIONS } from '../../../utils/functionalArea';
import { UnitOfMeasurementDropdown } from '../../common';

interface EditMeterDialogProps {
  open: boolean;
  onClose: () => void;
  meter: DiagnosisMetersReadDTO | null;
  diagnosisId: string;
  existingMeters?: DiagnosisMetersReadDTO[]; // Per calcolare l'index
  genericEnergyVectors?: EnergyVectorsReadDTO[]; // Vettori energetici generici passati dallo step
}

export const EditMeterDialog: React.FC<EditMeterDialogProps> = ({
  open,
  onClose,
  meter,
  diagnosisId,
  existingMeters = [],
  genericEnergyVectors = [],
}) => {
  const queryClient = useQueryClient();
  // Carica gli impianti di autoproduzione della diagnosi
  const { data: selfProductionPlants = [] } = useSelfProductionPlantsByDiagnosisId(diagnosisId);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<DiagnosisMetersCreateDTO & { unit_of_measurement?: string }>({
    defaultValues: meter
      ? {
          diagnosis_id: diagnosisId,
          table_code_index: String(meter.table_code_index ?? ''),
          meter_code: meter.meter_code ?? undefined,
          meter_customer_code: (meter as any).meter_customer_code ?? undefined,
          energy_vector_id: meter.energy_vector_id ?? undefined,
          unit_of_measurement: (meter as any).unit_of_measurement ?? undefined,
          type: meter.type ?? undefined,
          meter_type: (meter as any).meter_type ?? null,
          id_autoproduzione: (meter as any).id_autoproduzione ?? undefined,
          hierarchy: meter.hierarchy ?? undefined,
          functional_area: meter.functional_area ?? undefined,
          energy_use_id: (meter as any).energy_use_id ?? undefined,
          grouping: meter.grouping ?? undefined,
          notes: meter.notes ?? undefined,
        }
      : {
          diagnosis_id: diagnosisId,
          table_code_index: '',
        },
  });

  const selectedEnergyVectorId = watch('energy_vector_id');

  // Carica il vettore energetico selezionato per ottenere original_energy_vector_id
  const { data: selectedEnergyVector } = useQuery<DiagnosisEnergyVectorsReadDTO>(
    ['diagnosis-energy-vector', selectedEnergyVectorId],
    () => apiClient.get_item_diagnosis_energy_vectors__item_id__get(selectedEnergyVectorId!),
    { enabled: !!selectedEnergyVectorId }
  );

  const defaultUnitOfMeasurement = selectedEnergyVector?.unit_of_measurement;
  const selectedVectorType = selectedEnergyVector?.vector_type;
  const meterTypeOptions = React.useMemo(() => {
    if (selectedVectorType === 'Electricity') {
      return [
        { value: MeterType.PODp, label: 'PODp (Prelievo)' },
        { value: MeterType.PODi, label: 'PODi (Immissione)' },
        { value: MeterType.PROD, label: 'PROD (Autoproduzione)' },
      ];
    }
    if (selectedVectorType === 'NaturalGas') {
      return [{ value: MeterType.PDR, label: 'PDR (Gas)' }];
    }
    return [{ value: MeterType.TOT, label: 'TOT (Altro)' }];
  }, [selectedVectorType]);

  // Usa un ref per tracciare se il dialog è stato appena aperto
  const isInitialMount = React.useRef(true);
  const previousOpen = React.useRef(open);

  React.useEffect(() => {
    // Reset solo quando il dialog viene aperto (non quando cambia defaultUnitOfMeasurement)
    if (open && (isInitialMount.current || previousOpen.current !== open)) {
      if (meter) {
        reset({
          diagnosis_id: diagnosisId,
          table_code_index: String(meter.table_code_index ?? ''),
          meter_code: meter.meter_code ?? undefined,
          meter_customer_code: (meter as any).meter_customer_code ?? undefined,
          energy_vector_id: meter.energy_vector_id ?? undefined,
          unit_of_measurement: (meter as any).unit_of_measurement ?? undefined,
          type: meter.type ?? undefined,
          meter_type: (meter as any).meter_type ?? null,
          id_autoproduzione: (meter as any).id_autoproduzione ?? undefined,
          hierarchy: meter.hierarchy ?? undefined,
          functional_area: meter.functional_area ?? undefined,
          energy_use_id: (meter as any).energy_use_id ?? undefined,
          grouping: meter.grouping ?? undefined,
          notes: meter.notes ?? undefined,
        });
      } else {
        // Reset per nuovo contatore
        reset({
          diagnosis_id: diagnosisId,
          table_code_index: '',
          meter_code: undefined,
          meter_customer_code: undefined,
          energy_vector_id: undefined,
          unit_of_measurement: undefined, // Non usare defaultUnitOfMeasurement qui, verrà impostato quando si seleziona un vettore
          type: undefined,
          meter_type: null as any,
          id_autoproduzione: undefined,
          hierarchy: undefined,
          functional_area: undefined,
          energy_use_id: undefined,
          grouping: undefined,
          notes: undefined,
        });
      }
      isInitialMount.current = false;
    }
    previousOpen.current = open;
  }, [meter, diagnosisId, reset, open]);

  const createMutation = useMutation(
    async (data: DiagnosisMetersCreateDTO) => {
      return await apiClient.create_item_diagnosis_meters__post(data);
    },
    {
      onSuccess: (createdMeter: any) => {
        queryClient.invalidateQueries(`diagnosis-meters-${diagnosisId}`);
        // Invalida anche le query dei consumi per ricaricare la tabella dei costi
        queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
        queryClient.invalidateQueries({ queryKey: [`all-consumption-data-${diagnosisId}`] });
        invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
        toast.success('Contatore creato con successo');
        // Se è un contatore PODp, è stato creato un POD orario: aggiorna Grafici POD orari
        if (createdMeter?.meter_type === 'PODp') {
          queryClient.invalidateQueries(`pod-timetables-${diagnosisId}`);
          toast.success('Pod orario creato con successo');
        }
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore nella creazione del contatore');
      },
    }
  );

  const updateMutation = useMutation(
    async (data: DiagnosisMetersCreateDTO) => {
      if (!meter?.id) {
        throw new Error('ID contatore mancante');
      }
      return await apiClient.update_item_diagnosis_meters__item_id__put(meter.id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`diagnosis-meters-${diagnosisId}`);
        // Invalida anche le query dei consumi per ricaricare la tabella dei costi
        queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
        queryClient.invalidateQueries({ queryKey: [`all-consumption-data-${diagnosisId}`] });
        invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
        toast.success('Contatore aggiornato con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nell'aggiornamento del contatore");
      },
    }
  );

  const onSubmit = (data: DiagnosisMetersCreateDTO & { unit_of_measurement?: string }) => {
    // Assicurati che unit_of_measurement sia una stringa.
    // meter_type/id_autoproduzione: invia null esplicito per azzerare (il backend usa exclude_unset).
    const submitData: IDiagnosisMetersCreateDTO = {
      ...data,
      unit_of_measurement: data.unit_of_measurement ?? defaultUnitOfMeasurement,
      meter_type: data.meter_type ?? (null as any),
      id_autoproduzione: data.id_autoproduzione ?? (null as any),
    };

    if (meter) {
      updateMutation.mutate(submitData as DiagnosisMetersCreateDTO);
    } else {
      // Calcola l'indice per il nuovo contatore: ultimo indice + 1
      const maxIndex =
        existingMeters.length > 0 ? Math.max(...existingMeters.map(m => (m as any).index ?? 0)) : 0;
      const newIndex = maxIndex + 1;

      createMutation.mutate({
        ...submitData,
        index: newIndex,
      } as DiagnosisMetersCreateDTO);
    }
  };

  const handleClose = () => {
    if (!updateMutation.isLoading && !createMutation.isLoading) {
      reset();
      onClose();
    }
  };

  const isSubmitting = updateMutation.isLoading || createMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{meter ? 'Modifica contatore' : 'Nuovo contatore'}</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Codice"
                {...register('meter_code')}
                error={!!errors.meter_code}
                helperText={errors.meter_code?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="ID contatore cliente"
                {...register('meter_customer_code')}
                error={!!errors.meter_customer_code}
                helperText={errors.meter_customer_code?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="energy_vector_id"
                control={control}
                render={({ field, fieldState }) => (
                  <EnergyVectorDropdown
                    diagnosisId={diagnosisId}
                    label="Vettore energetico"
                    value={field.value ?? null}
                    onChange={(value, option) => {
                      field.onChange(value);
                      const newGenericVector = genericEnergyVectors.find(
                        gv => gv.id === (option as any)?.original_energy_vector_id
                      );
                      setValue(
                        'unit_of_measurement',
                        newGenericVector?.unit_of_measurement ?? undefined,
                        { shouldDirty: true }
                      );
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder="Seleziona il vettore energetico"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {defaultUnitOfMeasurement && (
                <Controller
                  name="unit_of_measurement"
                  control={control}
                  render={({ field, fieldState }) => (
                    <UnitOfMeasurementDropdown
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      defaultUnitOfMeasurement={defaultUnitOfMeasurement}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="meter_type"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel shrink>Tipo contatore</InputLabel>
                    <Select
                      value={field.value ?? ''}
                      displayEmpty
                      notched
                      onChange={e =>
                        field.onChange((e.target.value as any) === '' ? null : e.target.value)
                      }
                      label="Tipo contatore"
                    >
                      <MenuItem value="">Nessuno</MenuItem>
                      {meterTypeOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="id_autoproduzione"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel shrink>Impianto autoproduzione</InputLabel>
                    <Select
                      value={field.value ?? ''}
                      displayEmpty
                      notched
                      onChange={e => field.onChange(e.target.value === '' ? null : e.target.value)}
                      label="Impianto autoproduzione"
                      disabled={selfProductionPlants.length === 0}
                    >
                      <MenuItem value="">Nessuno</MenuItem>
                      {selfProductionPlants.map(plant => (
                        <MenuItem key={plant.id} value={plant.id}>
                          {plant.type || plant.id}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                    {selfProductionPlants.length === 0 && (
                      <FormHelperText>Nessun impianto di autoproduzione disponibile</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Gerarchia"
                {...register('hierarchy')}
                error={!!errors.hierarchy}
                helperText={errors.hierarchy?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!errors.functional_area}>
                <InputLabel>Area funzionale</InputLabel>
                <Controller
                  name="functional_area"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Area funzionale">
                      <MenuItem value="">
                        <em>Nessuna</em>
                      </MenuItem>
                      {FUNCTIONAL_AREA_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.functional_area && (
                  <FormHelperText>{errors.functional_area.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="energy_use_id"
                control={control}
                render={({ field, fieldState }) => (
                  <EnergyUseDropdown
                    diagnosisId={diagnosisId}
                    value={field.value ?? null}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Raggruppamento dato"
                {...register('grouping')}
                error={!!errors.grouping}
                helperText={errors.grouping?.message}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Note"
                {...register('notes')}
                error={!!errors.notes}
                helperText={errors.notes?.message}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" justifyContent="center" sx={{ width: '100%', py: 1 }}>
            <ActionButton
              icon={<SaveIcon />}
              tooltip={'Salva'}
              loading={isSubmitting}
              disabled={isSubmitting || !isDirty}
              buttonType="submit"
            />
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};
